import { ReactNode } from 'react'
import GlobalSearch from '../search/GlobalSearch'
import PrimarySidebar from './PrimarySidebar'
import MobileTopBar from './MobileTopBar'
import { MobileSecondaryPortal } from './MobileNavDrawer'
import MobileAiSheet from './MobileAiSheet'
import { useAppShellStore } from '../../stores/appShellStore'
import { useAuthStore } from '../../stores/authStore'
import { useActiveServer } from '../../hooks/useActiveServer'
import { useIsMobile } from '../../hooks/useBreakpoint'
import { isModuleEnabled } from '@os/contracts/shared'
import AIChatPanel from '@os/assistant/web/components/AIChatPanel'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { BirthdayNudge } from '../birthday/BirthdayNudge'

interface Props {
  children: ReactNode
  secondarySidebar?: ReactNode
  /**
   * @deprecated System-wide AI panel is now rendered by AppLayout itself
   * (toggled from the left rail's AI Assistant button). The `rightPanel`
   * slot is kept only as a fallback for callers that need a route-local
   * right panel; when omitted we render the global AIChatPanel instead.
   */
  rightPanel?: ReactNode
}

export default function AppLayout({ children, secondarySidebar, rightPanel }: Props) {
  const aiPanelOpen = useAppShellStore((s) => s.aiPanelOpen)
  const setAiPanelOpen = useAppShellStore((s) => s.setAiPanelOpen)
  const updateUser = useAuthStore((s) => s.updateUser)
  const secondarySidebarCollapsed = useAppShellStore((s) => s.secondarySidebarCollapsed)
  const setSecondarySidebarCollapsed = useAppShellStore((s) => s.setSecondarySidebarCollapsed)
  const isMobile = useIsMobile()

  // Gate the panel by the active server's 'assistant' subscription — the
  // same key PrimarySidebar uses to show/hide the footer toggle. AI Layer
  // is a separate 'ai' rail module now, toggled independently. Without this
  // gate the panel still renders when the assistant is disabled (e.g. a
  // freshly-registered user, whose isAiChatCollapsed is undefined →
  // aiPanelOpen defaults open in Chat.tsx) AND the toggle is hidden,
  // stranding it open.
  const aiEnabled = isModuleEnabled(useActiveServer()?.enabledModules, 'assistant')

  // OSPROD-AI-RAIL: the AI assistant button on the primary rail toggles
  // `aiPanelOpen` for ALL routes (Workspaces, Chats, Calendar, Mail,
  // Tracker, …). Previously each page had to pass its own `rightPanel`,
  // and Calendar/Mail/etc. didn't — so clicking the rail button looked
  // like a no-op on those routes. Render the global AIChatPanel here
  // unless the caller explicitly provides a route-specific panel.
  const panel = rightPanel ?? (
    <AIChatPanel onToggle={() => {
      const next = !aiPanelOpen
      setAiPanelOpen(next)
      // Mirror the legacy Chat behaviour so the user setting stays in
      // sync with the visible state (close → collapsed=true).
      updateUser({ settings: { isAiChatCollapsed: !next } })
    }} />
  )

  // W1.0 mobile shell (<1024). Desktop (≥1024) path below is unchanged.
  // NOTE: <MobileNavDrawer/> is intentionally NOT rendered here — it lives
  // once at App root so it survives navigation without re-animating. On mobile
  // we portal THIS route's secondary sidebar into the drawer's L2 host via
  // <MobileSecondaryPortal> (renders nothing while the drawer is closed), so
  // the drawer always shows the route's live rail-2 with no stale snapshot.
  if (isMobile) {
    // h-[100dvh] (not h-screen/100vh): on iOS Safari 100vh is the URL-bar-
    // hidden height, so a 100vh shell is taller than the visible area while
    // the address bar shows — pushing the composer / bottom bars below the
    // fold and making fixed chrome feel like it drifts as the bar collapses.
    // The dynamic viewport unit tracks the actually-visible height. Mobile-
    // only branch; desktop (below) keeps h-screen.
    return (
      <div
        className="h-[100dvh] w-screen flex flex-col overflow-hidden bg-background text-foreground"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <MobileTopBar />
        {/* Host for global banners portaled in-flow under the top bar.
            UpdateAvailableBadge (mounted once at App root) portals its mobile
            strip here so it pushes content within the 100dvh shell. */}
        <div id="rx-mobile-banner-host" className="shrink-0" />
        <BirthdayNudge />
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">{children}</div>
        {secondarySidebar && <MobileSecondaryPortal>{secondarySidebar}</MobileSecondaryPortal>}
        <MobileAiSheet open={aiPanelOpen && aiEnabled} onClose={() => setAiPanelOpen(false)}>
          {panel}
        </MobileAiSheet>
        <GlobalSearch />
      </div>
    )
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-background text-foreground">
      <BirthdayNudge />
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <PrimarySidebar />

        {/* OSPROD-102: collapsible channel / secondary sidebar (Discord-
            style). A thin handle on the divider toggles it so the call /
            screen-share area can use the full width. */}
        {secondarySidebar && !secondarySidebarCollapsed && (
          <div className="w-[360px] shrink-0 border-r border-border overflow-hidden flex flex-col bg-card">
            {secondarySidebar}
          </div>
        )}
        {/* Collapsed: a 12px muted strip was almost invisible, so users
            couldn't find how to bring the channel list back. Widen it and give
            the chevron full contrast + a brand-tint hover so it reads as a real
            affordance. Expanded: stays a thin, subtle divider handle. */}
        {secondarySidebar && (
          <button
            onClick={() => setSecondarySidebarCollapsed(!secondarySidebarCollapsed)}
            title={secondarySidebarCollapsed ? 'Show channels' : 'Hide channels'}
            aria-label={secondarySidebarCollapsed ? 'Show channels' : 'Hide channels'}
            className={`${
              secondarySidebarCollapsed
                ? 'w-6 text-foreground hover:bg-primary/10'
                : 'w-3 text-muted-foreground hover:bg-muted'
            } shrink-0 border-r border-border bg-card flex items-center justify-center hover:text-foreground transition`}
          >
            {secondarySidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={14} />}
          </button>
        )}

        <div className="flex-1 min-w-0 overflow-hidden flex flex-col">
          {children}
        </div>

        {aiPanelOpen && aiEnabled && panel}
      </div>

      <GlobalSearch />
    </div>
  )
}
